import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, DangerBtn, Loading, Msg, Section, panelGet, panelPost } from '../../kit';
import { AdminLink } from '../../adminNav';
import { Check, CheckList, Drawer, EmailPreview, Labeled, Modal, fmtFrom, inputCls } from '../emailKit';
import { DesignsMeta, bodyToEditorHtml } from '../designs/types';
import { ContentEditor, FallbacksPanel, fallbackable } from '../content/ContentEditor';
import { EnvelopeFields } from '../content/EnvelopeFields';
import { SaveAsTemplate, TemplatePicker } from '../content/TemplatePicker';
import {
  EMPTY_EDITABLE, EditableContent, Envelope, FORMAT_LABEL, FOUNDERS_INBOX, Rendered, Sender, SourceContent, SourceRef,
  TEAM_INBOX, contentProblem, defaultEnvelope, fromSource, renderedFor, toTriggerContent, withKeys,
} from '../content/types';
import {
  EMPTY_TAG_FILTER, TagFilter, TagFilterValue, describeTagFilter, tagFilterActive, tagFilterFromParams, tagFilterParams,
} from '../../tags/shared';
import {
  Banner, ConfirmDialog, Flash, LIBRARY_KEY_PREFIX, MEMAC_KEY_PREFIX, Overview, ParamSpec, Pill, SECTION, STATE_UNKNOWN,
  StatePill, Trigger, TriggerTemplateOption, capText, categoryLabel, closedGates, fmtIST, inBody, list, num, obj, windowText,
} from './shared';
import { TriggerSwitch, contentText } from './Switches';
import { DryRun, DryRunView, SendNow, normDryRun } from './DryRun';
import { VaryingNotices } from '../VaryingNotice';
import { NextSendPanel, Schedules } from './Schedule';

/**
 * One trigger, opened from the Triggers table (or "New trigger"): its switch
 * and what it is doing right now (the Leads → AlertCard pattern), the editor
 * — signal + parameters, audience, content, caps, category — and the checks:
 * Dry run (who would get it now, with reasons), Send a test to me, Send to
 * these N now (typed count), and Run now for an automatic one.
 *
 * Saving never touches On/Auto: those go through the switch and its confirm
 * (email/memac_trigger_toggle). Built-in triggers can be edited and reset to
 * the code's values (the switch stays as it is), not deleted; custom ones
 * can be deleted. External rows (mail sent by other code) are read-only here.
 *
 * What it sends (owner, 2026-10-14: reuse the campaigns' and the saved
 * templates, and write new ones with the Campaigns editor): either a saved
 * template by reference — a MEMAC built-in, a Saved template shared with
 * Campaigns (lib_…) or any other e-mail; an edit to it reaches this trigger
 * on its next send — or the trigger's own copy, written with the shared
 * ContentEditor (rich text or design) and its From / Reply-To. "Start from…"
 * fills either one from a saved template, a built-in e-mail, a past campaign
 * or a design; an own copy can be saved as a new Saved template. Preview
 * renders the unsaved draft as the trigger would send it (footer included,
 * sample links, nothing written or sent: email/memac_preview).
 *
 * "Next automatic send" (email/memac_schedule): the trigger's real timing and
 * a countdown to the run at which it would next e-mail someone on its own —
 * live when every gate is open, "if enabled" with the closed gates otherwise.
 * "Preview by cohort" opens the mail each status × cohort segment would get
 * now (email/memac_cohort_preview; the saved trigger, nothing sent).
 */

// ---------------------------------------------------------------------------
// Draft
// ---------------------------------------------------------------------------
type Draft = {
  name: string; category: string; applies_to: string;
  signal: string; params: Record<string, string>; stop_signal: string; stop_params: Record<string, string>;
  tf: TagFilterValue; cohort: string[];
  content_mode: 'template' | 'design' | 'custom'; template_key: string;
  /** The trigger's own copy: rich text (content_mode custom) or a design. It
   *  stays stored while the trigger sends a template, so switching back
   *  loses nothing. */
  copy: EditableContent; env: Envelope;
  /** Where the copy was started from (content.source), and the trigger's fallbacks ({first_name} ->
   *  "there"): with its own copy, and over a saved template's own while it sends one. */
  source: SourceRef | null; fallbacks: Record<string, string>;
  cta_label: string;
  max_per_30d: string; max_lifetime: string; cooldown_days: string; per_occurrence: string;
  notes: string;
};

/** Audience keys the editor owns; anything else in the stored audience is kept as it is. */
const AUDIENCE_KEYS = new Set(['lifecycle', 'status', 'statuses', 'cohort', 'cohorts', 'tags', 'tags_all', 'not_tags',
  'invite_sent', 'signed_in', 'persona', 'account_origin', 'profile_min', 'profile_max']);
const isAudienceKey = (k: string) => AUDIENCE_KEYS.has(k) || /_(from|to|empty)$/.test(k);
const LIST_KEYS = ['lifecycle', 'tags', 'tags_all', 'not_tags', 'persona', 'account_origin'];

const kindOf = (p: ParamSpec | undefined, v: any): string => {
  const t = (p?.type || '').toLowerCase();
  if (/bool/.test(t)) return 'bool';
  if (/list|array|\[\]/.test(t)) return 'list';
  if (/int|num|float|day|hour|minute/.test(t)) return 'number';
  if (/json|object|dict/.test(t)) return 'json';
  if (/select|enum|choice/.test(t) || (p?.options && p.options.length)) return 'select';
  const d = v ?? p?.default;
  if (typeof d === 'boolean') return 'bool';
  if (Array.isArray(d)) return 'list';
  if (typeof d === 'number') return 'number';
  if (d && typeof d === 'object') return 'json';
  return 'text';
};
const paramText = (v: any, kind: string): string => {
  if (v === null || v === undefined) return '';
  if (kind === 'list') return Array.isArray(v) ? v.join(', ') : String(v);
  if (kind === 'json') return typeof v === 'string' ? v : JSON.stringify(v);
  if (kind === 'bool') return v === true || v === 'true' || v === 1 ? 'true' : 'false';
  return String(v);
};
/** The draft's text back to typed values; throws a readable error. */
const paramValue = (name: string, s: string, kind: string): any => {
  const t = s.trim();
  if (kind === 'bool') return t === 'true';
  if (t === '') return undefined;
  if (kind === 'number') {
    const n = Number(t);
    if (!Number.isFinite(n)) throw new Error(`“${name}” must be a number.`);
    return n;
  }
  if (kind === 'list') {
    const parts = t.split(',').map(x => x.trim()).filter(Boolean);
    return parts.every(x => x !== '' && Number.isFinite(Number(x))) ? parts.map(Number) : parts;
  }
  if (kind === 'json') {
    try { return JSON.parse(t); } catch { throw new Error(`“${name}” must be valid JSON.`); }
  }
  return t;
};

const draftFrom = (t: Trigger | null, ov: Overview | null): Draft => {
  const sig = t?.signal || '';
  const spec = ov?.signals.find(s => s.key === sig);
  const params: Record<string, string> = {};
  (spec?.params || []).forEach(p => {
    const v = t?.signal_params?.[p.name];
    params[p.name] = paramText(v === undefined ? p.default : v, kindOf(p, v));
  });
  const a = t?.audience || {};
  const tf = tagFilterFromParams({ ...a, lifecycle: a.lifecycle ?? a.status ?? a.statuses });
  return {
    name: t?.name || '',
    category: t?.category || 'user_activity',
    applies_to: t?.applies_to || '',
    signal: sig,
    params,
    stop_signal: t?.stop_signal || '',
    stop_params: stopParamsFrom(t?.stop_signal || '', t?.raw?.stop_signal, ov),
    tf,
    cohort: list(a.cohort ?? a.cohorts).map(x => x.toUpperCase()),
    content_mode: t?.content_mode || 'template',
    template_key: t?.template_key || '',
    ...copyFrom(t),
    cta_label: t?.cta_label || '',
    max_per_30d: t?.max_per_30d === null || t?.max_per_30d === undefined ? '' : String(t.max_per_30d),
    max_lifetime: t?.max_lifetime === null || t?.max_lifetime === undefined ? '' : String(t.max_lifetime),
    cooldown_days: t?.cooldown_days === null || t?.cooldown_days === undefined ? '' : String(t.cooldown_days),
    per_occurrence: t?.per_occurrence === null || t?.per_occurrence === undefined ? '' : String(t.per_occurrence),
    notes: t?.notes || '',
  };
};
/** Equal drafts; a design's client-side block keys (_k) are not content. */
const sameDraft = (a: Draft, b: Draft) => {
  const plain = (d: Draft) => JSON.stringify(d, (k, v) => (k === '_k' ? undefined : v));
  return plain(a) === plain(b);
};

/** The stored content as the editor holds it. A body written before the
 *  editor (plain text) opens as its paragraphs, so opening it is no edit. */
const copyFrom = (t: Trigger | null): Pick<Draft, 'copy' | 'env' | 'source' | 'fallbacks'> => {
  const c = t?.content || {};
  const isDesign = t?.content_mode === 'design' || (t?.content_mode !== 'custom' && !!c.design);
  const raw = String(c.body ?? c.html ?? '');
  const rawHtml = !isDesign && /<html[\s>]/i.test(raw);
  const doc = c.design && typeof c.design === 'object' && Array.isArray(c.design.blocks) ? c.design : null;
  const src = obj(c.source);
  const fb = obj(c.placeholder_fallbacks);
  return {
    copy: {
      ...EMPTY_EDITABLE,
      format: isDesign ? 'design' : 'html',
      subject: String(c.subject ?? ''),
      body: isDesign ? '' : rawHtml ? raw : bodyToEditorHtml(raw),
      text: isDesign ? '' : String(c.text ?? ''),
      design: isDesign && doc ? withKeys(doc) : null,
      design_from: String(c.design_from ?? ''),
      raw_html: rawHtml,
    },
    env: { from_email: String(c.from_email ?? ''), from_name: String(c.from_name ?? ''), reply_to: String(c.reply_to ?? '') },
    source: src.kind ? {
      kind: String(src.kind), ref: String(src.ref ?? ''), name: String(src.name || src.ref || ''), at: String(src.at ?? ''),
      ...(Number.isFinite(Number(src.version)) && src.version !== null && src.version !== '' ? { version: Number(src.version) } : {}),
    } : null,
    fallbacks: Object.fromEntries(Object.entries(fb).map(([k, v]) => [k, String(v ?? '')])),
  };
};
const copyEmpty = (c: EditableContent) =>
  !c.subject.trim() && !c.body.replace(/<[^>]*>/g, '').trim() && !(c.design?.blocks?.length);

/** A new copy's sender, as the MEMAC templates have it (memac_templates' preset):
 *  Founders <inbox@>, replies to info@ — also before the approved list is read. */
const foundersEnvelope = (senders: Sender[]): Envelope => (senders.length ? defaultEnvelope(senders)
  : { from_email: FOUNDERS_INBOX, from_name: 'Founders at UniversityTech.io', reply_to: TEAM_INBOX });

const SOURCE_KIND_LABEL: Record<string, string> = {
  library: 'the saved template', template: 'the e-mail', campaign: 'the campaign', design: 'the design',
  trigger: 'the trigger', blank: 'a blank e-mail',
};

/** A read the editor makes only once it is needed (the own-copy editor's designs and senders). */
const useLazyGet = <T,>(action: string, when: boolean) => {
  const [st, setSt] = useState<{ data: T | null; error: string; loading: boolean }>({ data: null, error: '', loading: false });
  const asked = useRef(false);
  useEffect(() => {
    if (!when || asked.current) return;
    asked.current = true;
    setSt(x => ({ ...x, loading: true }));
    void panelGet(SECTION, action).then(({ data, error }) => setSt({ data: data as T, error, loading: false }));
  }, [when, action]);
  return st;
};

/** A stop condition's parameters (e.g. profile_raw_above → pct), as text. */
const stopSchema = (key: string, ov: Overview | null): ParamSpec[] => {
  const raw = ov?.raw?.stop_signals;
  const hit = Array.isArray(raw) ? raw.find((x: any) => x && typeof x === 'object' && x.key === key) : null;
  const list = Array.isArray(hit?.params_schema) ? hit.params_schema : [];
  return list.filter((p: any) => p && p.name).map((p: any) => ({
    name: String(p.name), type: String(p.type || ''), default: p.default, help: String(p.help || ''),
    options: Array.isArray(p.options) ? p.options.map(String) : undefined,
  }));
};
const stopParamsFrom = (key: string, raw: any, ov: Overview | null): Record<string, string> => {
  const out: Record<string, string> = {};
  const stored = raw && typeof raw === 'object' && raw.signal === key ? raw : {};
  stopSchema(key, ov).forEach(p => {
    const v = stored[p.name];
    out[p.name] = paramText(v === undefined ? p.default : v, kindOf(p, v));
  });
  return out;
};
/** Categories memac treats as service (exempt from the status cap and unsubscribes). */
const SERVICE_CATS = ['service', 'billing'];

const capInt = (label: string, s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isInteger(n) || n < 0) throw new Error(`${label} must be a whole number (or blank for none).`);
  return n;
};

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------
const TriggerEditor: React.FC<{
  /** A trigger id, or '' for a new one. */
  id: string;
  ov: Overview | null;
  triggers: Trigger[];
  /** The template picker's list (memac_triggers.templates): light, no bodies. */
  templates: TriggerTemplateOption[];
  /** The list could not be read (the picker then lists only the current key). */
  templatesError?: string;
  onClose: () => void;
  /** After a save / switch / reset / delete: reload the list; `openId` = show that trigger. */
  onChanged: (openId?: string | null) => void;
  /** MEMAC → Templates, on that template when a key is given (a Saved one opens under Saved templates). */
  onOpenTemplates: (key?: string) => void;
  onOpenRuns: (id: string) => void;
  onOpenTrigger: (id: string) => void;
  /** memac_schedule (the countdowns); optional so the editor works without it. */
  sch?: Schedules;
  onElapsed?: () => void;
  onPreviewCohorts?: (id: string) => void;
}> = ({ id, ov, triggers, templates, templatesError, onClose, onChanged, onOpenTemplates, onOpenRuns, onOpenTrigger,
  sch, onElapsed, onPreviewCohorts }) => {
  const t = id ? triggers.find(x => x.id === id) || null : null;
  const isNew = !id;
  const [note, setNote] = useState<Flash>(null);
  const [richNote, setRichNote] = useState<{ ok: boolean; node: React.ReactNode } | null>(null);

  // Same draft handling as Leads → AlertCard: `dirty` keeps a half-typed
  // edit from being overwritten by a refetch; `seededFrom` re-seeds only when
  // the saved trigger itself changes.
  const [draft, setDraft] = useState<Draft>(() => draftFrom(t, ov));
  const [dirty, setDirty] = useState(false);
  // Re-seeded when the saved trigger or the signal catalogue (overview)
  // arrives or changes — never over an edit in progress.
  const seededFrom = useRef<{ raw: any; ov: any }>({ raw: t?.raw ?? null, ov });
  useEffect(() => {
    if (dirty) return;
    if (seededFrom.current.raw === (t?.raw ?? null) && seededFrom.current.ov === ov) return;
    seededFrom.current = { raw: t?.raw ?? null, ov };
    setDraft(draftFrom(t, ov));
  }, [t?.raw, ov, dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const edit = (patch: Partial<Draft>) => { setDraft(d => ({ ...d, ...patch })); setDirty(true); };
  // The editor's own copy: a format change (rich text <-> design) is the content mode too.
  const editCopy = (patch: Partial<EditableContent>) => {
    setDraft(d => {
      const copy = { ...d.copy, ...patch };
      return { ...d, copy, content_mode: d.content_mode === 'template' ? 'template' : copy.format === 'design' ? 'design' : 'custom' };
    });
    setDirty(true);
  };
  const editEnv = (patch: Partial<Envelope>) => { setDraft(d => ({ ...d, env: { ...d.env, ...patch } })); setDirty(true); };
  const discard = () => { setDraft(draftFrom(t, ov)); setDirty(false); setNote(null); };
  const base = useMemo(() => draftFrom(t, ov), [t?.raw, ov]); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = dirty && !sameDraft(draft, base);

  // Templates for the picker: MEMAC's own first, then the Saved templates
  // (shared with Campaigns), then every other e-mail — grouped as filed.
  const tplGroups = useMemo(() => {
    const rank = (x: TriggerTemplateOption) => (x.key.startsWith(MEMAC_KEY_PREFIX) ? 0 : x.library ? 1 : 2);
    const out: { group: string; items: TriggerTemplateOption[] }[] = [];
    [...templates].sort((a, b) => rank(a) - rank(b)).forEach(x => {
      const name = x.group || (x.library ? 'Saved templates' : 'Other');
      let g = out.find(o => o.group === name);
      if (!g) { g = { group: name, items: [] }; out.push(g); }
      g.items.push(x);
    });
    return out;
  }, [templates]);
  const tpl = templates.find(x => x.key === draft.template_key) || null;
  // Other triggers that send the picked template: an edit to it changes theirs too.
  const alsoUsing = draft.template_key
    ? triggers.filter(x => x.id !== t?.id && !x.external && x.content_mode === 'template' && x.template_key === draft.template_key)
    : [];

  // The own-copy editor's designs, placeholders and senders: read once it is needed.
  const ownCopy = draft.content_mode !== 'template';
  const [wantCopy, setWantCopy] = useState(ownCopy);
  useEffect(() => { if (ownCopy) setWantCopy(true); }, [ownCopy]);
  const designs = useLazyGet<DesignsMeta>('designs', wantCopy);
  // Template mode: the placeholders the picked template uses, and what each
  // becomes without this trigger's own fallback (a saved template's own, else
  // the defaults) — email/target_placeholders, which reads only.
  const phKey = !ownCopy && draft.template_key ? `${draft.template_key}@${tpl?.version ?? ''}` : '';
  const [tplPh, setTplPh] = useState<{ key: string; names: string[]; inherited: Record<string, string>; error: string } | null>(null);
  useEffect(() => {
    if (!phKey || tplPh?.key === phKey) return undefined;
    let gone = false;
    void panelPost(SECTION, 'target_placeholders', { content_mode: 'template', key: draft.template_key }).then(({ data, error }) => {
      if (gone) return;
      const rows: any[] = Array.isArray(data?.placeholders) ? data.placeholders : [];
      const person = rows.filter(r => r && r.kind === 'person' && r.name);
      setTplPh({
        key: phKey, names: person.map(r => String(r.name)), error: error || '',
        inherited: Object.fromEntries(person.map(r => [String(r.name), String(r.default_fallback ?? '')])),
      });
    });
    return () => { gone = true; };
  }, [phKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const sendersRes = useLazyGet<{ senders?: Sender[] }>('senders', wantCopy);
  const senders: Sender[] = Array.isArray(sendersRes.data?.senders) ? sendersRes.data!.senders! : [];
  const placeholders = designs.data?.placeholders || [];

  const signals = ov?.signals || [];
  const sig = signals.find(s => s.key === draft.signal);
  const stopOpts: { key: string; label: string }[] = useMemo(() => {
    const raw = ov?.raw?.stop_signals;
    if (Array.isArray(raw)) {
      return raw.map((x: any) => typeof x === 'string' ? { key: x, label: x } : { key: String(x.key), label: String(x.label || x.key) });
    }
    return [];
  }, [ov]);

  const setSignal = (key: string) => {
    const spec = signals.find(s => s.key === key);
    const params: Record<string, string> = {};
    (spec?.params || []).forEach(p => { params[p.name] = paramText(p.default, kindOf(p, p.default)); });
    edit({ signal: key, params });
  };

  // -- save -----------------------------------------------------------------
  const [busy, setBusy] = useState('');
  const buildBody = (): Record<string, any> => {
    if (!draft.name.trim()) throw new Error('Give the trigger a name.');
    if (!draft.signal) throw new Error('Pick the signal that fires it.');
    if (draft.content_mode === 'template' && !draft.template_key) throw new Error('Pick the template it sends.');
    if (draft.content_mode !== 'template') {
      const problem = contentProblem(draft.copy);
      if (problem) throw new Error(problem);
    }
    const params: Record<string, any> = { ...(t?.signal_params && t.signal === draft.signal ? t.signal_params : {}) };
    (sig?.params || []).forEach(p => {
      const v = paramValue(p.name, draft.params[p.name] ?? '', kindOf(p, p.default));
      if (v === undefined) delete params[p.name]; else params[p.name] = v;
    });
    // Audience: the editor's keys rebuilt, everything else kept.
    const audience: Record<string, any> = {};
    Object.entries(t?.audience || {}).forEach(([k, v]) => { if (!isAudienceKey(k)) audience[k] = v; });
    Object.entries(tagFilterParams(draft.tf)).forEach(([k, v]) => {
      audience[k] = LIST_KEYS.includes(k) ? v.split(',').map(x => x.trim()).filter(Boolean) : v;
    });
    if (draft.cohort.length) audience.cohort = draft.cohort;
    // A stop condition keeps its extra keys.
    const stopBody: Record<string, any> | null = draft.stop_signal ? { signal: draft.stop_signal } : null;
    if (stopBody) {
      stopSchema(draft.stop_signal, ov).forEach(p => {
        const v = paramValue(p.name, draft.stop_params[p.name] ?? '', kindOf(p, p.default));
        if (v !== undefined) stopBody[p.name] = v;
      });
    }
    // The own copy as memac stores it. In template mode none is sent: the
    // server keeps the copy already stored (unused while it sends the template),
    // and the fallbacks go on their own.
    const own = draft.content_mode === 'template' ? null : toTriggerContent(draft.copy, draft.env, {
      source: draft.source, fallbacks: draft.fallbacks, name: t?.content?.name ? String(t.content.name) : undefined,
    });
    return {
      ...(isNew ? {} : { id: t!.id }),
      name: draft.name.trim(),
      category: draft.category,
      applies_to: draft.applies_to.trim(),
      signal: draft.signal,
      signal_params: params,
      stop_signal: stopBody,
      audience,
      content_mode: own ? own.content_mode : 'template',
      template_key: own ? null : draft.template_key,
      content: own ? own.content : null,
      // Template mode: the trigger's own fallbacks, over the template's (stored with its copy).
      ...(own ? {} : { placeholder_fallbacks: draft.fallbacks }),
      cta_label: draft.cta_label.trim(),
      max_per_30d: capInt('Max per 30 days', draft.max_per_30d),
      max_lifetime: capInt('Max in a lifetime', draft.max_lifetime),
      cooldown_days: capInt('Cooldown days', draft.cooldown_days),
      per_occurrence: capInt('Per occurrence', draft.per_occurrence),
      notes: draft.notes,
    };
  };
  const save = async () => {
    let body: Record<string, any>;
    try { body = buildBody(); } catch (e: any) { setNote({ kind: 'error', text: e.message }); return; }
    setBusy('save'); setNote(null); setRichNote(null);
    const { data, error } = await panelPost(SECTION, 'memac_trigger_save', body);
    setBusy('');
    if (error) { setNote({ kind: 'error', text: error }); return; }
    const newId = String(data?.trigger?.id || data?.id || t?.id || '');
    setDirty(false);
    setDry(null);
    setNote({ kind: 'ok', text: isNew ? `Created ${newId || 'the trigger'} — it is Off and Manual until someone switches it.`
      : 'Saved. The switch is unchanged.' });
    onChanged(newId || null);
  };

  const reset = async () => {
    if (!t) return;
    setBusy('reset'); setNote(null);
    const { error } = await panelPost(SECTION, 'memac_trigger_reset', { trigger_id: t.id });
    setBusy('');
    if (error) { setNote({ kind: 'error', text: error }); return; }
    setDirty(false); setDry(null);
    setNote({ kind: 'ok', text: 'Reset to the built-in values. The switch is unchanged.' });
    onChanged(t.id);
  };
  const del = async () => {
    if (!t) return;
    setBusy('delete'); setNote(null);
    const { error } = await panelPost(SECTION, 'memac_trigger_delete', { trigger_id: t.id });
    setBusy('');
    if (error) { setNote({ kind: 'error', text: error }); return; }
    onChanged(null);
    onClose();
  };

  // -- what it sends: start from, copy, save as a template ---------------------
  const [picker, setPicker] = useState(false);
  // A built-in (plain text) e-mail with buttons, being copied: as a design or as rich text?
  const [askFormat, setAskFormat] = useState<SourceContent | null>(null);
  const [saveAs, setSaveAs] = useState(false);
  // Just saved as a template: offer to send that instead of the copy.
  const [savedTpl, setSavedTpl] = useState<{ key: string; name: string; warnings: string[] } | null>(null);

  /** Writing the trigger's own copy: a new one is from Founders <inbox@>, replies to info@. */
  const writeOwnCopy = () => {
    setDraft(d => ({
      ...d, content_mode: d.copy.format === 'design' ? 'design' : 'custom',
      env: d.env.from_email || !copyEmpty(d.copy) ? d.env : foundersEnvelope(senders),
    }));
    setDirty(true);
  };
  /** A source (in rich text or design) becomes the trigger's own copy, with its envelope and fallbacks. */
  const applyCopy = (src: SourceContent) => {
    const f = fromSource(src);
    const words = [...f.warnings];
    if (f.category && f.category !== draft.category) {
      words.push(`It was written as ${categoryLabel(f.category, ov?.categories || []).toLowerCase()} mail; this trigger `
        + `sends it as ${categoryLabel(draft.category, ov?.categories || []).toLowerCase()}.`);
    }
    setDraft(d => ({
      ...d,
      content_mode: f.content.format === 'design' ? 'design' : 'custom',
      copy: { ...f.content, ctas: [] },
      env: f.envelope.from_email ? f.envelope : d.env.from_email ? d.env : foundersEnvelope(senders),
      source: f.source,
      fallbacks: f.fallbacks,
    }));
    setDirty(true);
    setWantCopy(true);
    setNote({ kind: 'ok', text: `Copied “${src.name}” into this trigger — save to keep it.${words.length ? ` ${words.join(' ')}` : ''}` });
  };
  /** library_source in the format asked for (the server converts: it keeps literal braces right). */
  const fetchSource = async (kind: string, ref: string, as?: 'html' | 'design'): Promise<SourceContent | null> => {
    setBusy('source'); setNote(null); setRichNote(null);
    const { data, error } = await panelGet(SECTION, 'library_source', { kind, ref, ...(as ? { as } : {}) });
    setBusy('');
    if (error || !data) { setNote({ kind: 'error', text: `Could not read it: ${error || 'no answer'}` }); return null; }
    return data as SourceContent;
  };
  // The own copy is kept while a template is sent, so replacing it always asks.
  const confirmReplace = (what: string) => copyEmpty(draft.copy)
    || window.confirm(`Replace this trigger's own e-mail (“${draft.copy.subject || 'no subject'}”) with ${what}? `
      + 'What it says now is lost once you save.');
  /** Copy any source in: a plain-text one is converted on the server (asking first when it has buttons). */
  const takeCopy = async (src: SourceContent) => {
    if (src.format === 'html' || src.format === 'design') {
      if (confirmReplace(`“${src.name}”`)) applyCopy(src);
      return;
    }
    if ((src.ctas || []).some(c => c.label && c.url)) { setAskFormat(src); return; }
    const conv = await fetchSource(src.kind, src.ref, 'html');
    if (conv && confirmReplace(`“${src.name}”`)) applyCopy(conv);
  };
  const takeAs = async (src: SourceContent, as: 'html' | 'design') => {
    setAskFormat(null);
    const conv = await fetchSource(src.kind, src.ref, as);
    if (conv && confirmReplace(`“${src.name}”`)) applyCopy(conv);
  };
  const onPicked = (src: SourceContent, how: 'reference' | 'copy') => {
    setPicker(false);
    if (how === 'reference' && src.template_key) {
      edit({ content_mode: 'template', template_key: src.template_key });
      setNote({ kind: 'ok', text: `Sends “${src.name}” as it stands in its template — an edit to that template reaches `
        + 'this trigger on its next send. Save to keep the change.' });
      return;
    }
    void takeCopy(src);
  };
  /** Template mode → its words as this trigger's own copy. */
  const copyTemplateIn = async () => {
    if (!draft.template_key) return;
    const src = await fetchSource(tpl?.library || draft.template_key.startsWith(LIBRARY_KEY_PREFIX) ? 'library' : 'template',
      draft.template_key);
    if (src) void takeCopy(src);
  };
  // The saved template the copy was started from has moved on since.
  const srcNow = draft.source?.kind === 'library' ? templates.find(x => x.key === draft.source!.ref) || null : null;
  const copyStale = !!(srcNow?.version && draft.source?.version && srcNow.version > draft.source.version);
  const takeNewVersion = async () => {
    if (!draft.source) return;
    const src = await fetchSource('library', draft.source.ref, draft.copy.format === 'design' ? 'design' : 'html');
    if (!src) return;
    if (!copyEmpty(draft.copy) && !window.confirm(`Replace this trigger's copy with the current “${src.name}”`
      + `${srcNow?.version ? ` (v${srcNow.version})` : ''}? Changes made to the copy here are lost.`)) return;
    applyCopy(src);
  };
  const leaveFor = (go: () => void) => {
    if (changed && !window.confirm('Leave this trigger without saving your changes?')) return;
    go();
  };

  // -- preview (the unsaved draft, as the trigger would send it) ----------------
  const [pv, setPv] = useState<{ r: Rendered; sig: string } | null>(null);
  const [pvOpen, setPvOpen] = useState(false);
  const [pvAs, setPvAs] = useState('');
  const [pvErr, setPvErr] = useState('');
  const previewBody = (): Record<string, any> => {
    const d: Record<string, any> = { content_mode: draft.content_mode, category: draft.category };
    if (draft.content_mode === 'template') {
      d.template_key = draft.template_key;
      d.placeholder_fallbacks = draft.fallbacks;
    } else {
      const own = toTriggerContent(draft.copy, draft.env, { source: draft.source, fallbacks: draft.fallbacks });
      d.content_mode = own.content_mode;
      d.content = own.content;
    }
    const who = pvAs.trim().toLowerCase();
    return { ...(t ? { trigger_id: t.id } : {}), draft: d, ...(who ? { user_email: who } : {}) };
  };
  const pvSig = pvOpen ? JSON.stringify(previewBody()) : '';
  const preview = async () => {
    setPvOpen(true); setPvErr('');
    if (draft.content_mode === 'template' && !draft.template_key) { setPvErr('Pick a template first.'); return; }
    if (draft.content_mode !== 'template') {
      const problem = contentProblem(draft.copy);
      if (problem) { setPvErr(problem); return; }
    }
    const body = previewBody();
    setBusy('preview');
    const { data, error } = await panelPost(SECTION, 'memac_preview', body);
    setBusy('');
    if (error || !data) { setPvErr(error || 'No answer'); return; }
    setPv({ r: data as Rendered, sig: JSON.stringify(body) });
  };

  // -- checks ---------------------------------------------------------------
  const [dry, setDry] = useState<DryRun | null>(null);
  const runDry = async () => {
    if (!t) return;
    setBusy('dry'); setNote(null); setRichNote(null);
    const { data, error } = await panelGet(SECTION, 'memac_dry_run', { trigger_id: t.id });
    setBusy('');
    if (error) { setNote({ kind: 'error', text: `Dry run failed: ${error}` }); return; }
    setDry(normDryRun(data));
  };
  const test = async () => {
    if (!t) return;
    setBusy('test'); setNote(null); setRichNote(null);
    const { data, error } = await panelPost(SECTION, 'memac_test_send', { trigger_id: t.id });
    setBusy('');
    if (error || data?.success === false || data?.sent === false) {
      setNote({ kind: 'error', text: `Test not sent: ${error || data?.error || 'unknown error'}` }); return;
    }
    setNote({ kind: 'ok', text: `Test sent to ${data?.to || data?.sent_to || 'your own address'} only`
      + `${data?.subject ? ` (“${data.subject}”)` : ''}, filled in from ${data?.rendered_for || 'example values'}. Nobody else got it.` });
  };
  const [runAsk, setRunAsk] = useState(false);
  const [runErr, setRunErr] = useState('');
  const runNow = async () => {
    if (!t) return;
    setBusy('run'); setRunErr('');
    const { data, error } = await panelPost(SECTION, 'memac_run_now', { trigger_id: t.id });
    setBusy('');
    if (error) { setRunErr(error); return; }
    setRunAsk(false);
    // memac.process_due: {sent, failed, skipped?, error?, triggers: [{trigger_id, eligible, sent, failed}]}
    const item = (Array.isArray(data?.triggers) ? data.triggers : []).find((x: any) => x?.trigger_id === t.id) || {};
    const skipped = data?.skipped || data?.error || item.error;
    setRichNote({ ok: !skipped, node: skipped
      ? <>Nothing was sent: {String(skipped)}.</>
      : <>Run finished: {num(data?.sent ?? 0)} sent{data?.failed ? `, ${num(data.failed)} failed` : ''}
        {item.eligible !== undefined ? ` (${num(item.eligible)} were eligible)` : ''}.</> });
    onChanged(t.id);
  };

  // null = the MEMAC status was not read: an auto trigger's state is then unknown (amber).
  const gatesClosed = closedGates(ov, 1);
  const s = ov?.settings;
  const autoUnknown = !!t && !t.on_hold && t.enabled && t.mode === 'auto' && gatesClosed === null;
  const rightNow = (): string => {
    if (!t) return '';
    if (t.on_hold) return `on hold — ${t.hold_reason || 'it cannot be switched on yet'}`
      + `${t.can_send ? ' It can still be sent by hand.' : ''}`;
    if (!t.enabled) return 'off — nobody is e-mailed. Dry run, Send a test and Send to these N work while it is off.';
    if (t.mode === 'manual') return 'on, manual — nobody is e-mailed until someone presses Send to these N now.';
    if (gatesClosed === null) return `on, auto — ${STATE_UNKNOWN}, so it may be e-mailing every matching member on each run.`;
    if (gatesClosed.length) return `on, auto — but nothing is sent, because ${gatesClosed.join(' and ')}.`;
    return `on, auto — every matching member is e-mailed on each run (every ${s?.interval_minutes ?? '?'} min, ${s ? windowText(s) : ''}).`;
  };

  // -- external (read-only) -------------------------------------------------
  if (t && (t.external || t.read_only)) {
    return (
      <Drawer title={`${t.id} · ${t.name}`} onClose={onClose}
        subtitle={t.external ? 'Sent by other code — read-only in MEMAC' : `The same mail as ${t.alias_of} — read-only here`}>
        {t.alias_of && (
          <Banner tone="info">
            {t.id} is another name for <b>{t.alias_of}</b>: it is the same mail, so it is switched, edited and sent
            there.{' '}
            <button type="button" className="underline underline-offset-2 text-primary-700" onClick={() => onOpenTrigger(t.alias_of)}>
              Open {t.alias_of}</button>
          </Banner>
        )}
        {t.external && <Section title="Live elsewhere">
          <div className="flex items-center gap-2 flex-wrap"><StatePill t={t} />
            {t.ext?.on === true && <span className="text-xs text-grayscale-1100">switched on where it lives</span>}
            {t.ext?.on === false && <span className="text-xs text-grayscale-1100">switched off where it lives</span>}
          </div>
          <p className="text-sm text-grayscale-1300">
            This mail is already sent automatically by existing code. MEMAC lists it so the matrix is complete and
            never sends it — switching it here would double-send.
          </p>
          <div className="text-sm"><span className="text-grayscale-1100">Its switch: </span>{t.ext?.where}
            {t.ext?.link && <> · <AdminLink to={t.ext.link}>open</AdminLink></>}</div>
          {t.templateKeys.length > 0 && (
            <div className="text-sm"><span className="text-grayscale-1100">Template{t.templateKeys.length > 1 ? 's' : ''}: </span>
              {t.templateKeys.map((k, i) => <React.Fragment key={k}>{i > 0 && ', '}
                <AdminLink to={{ group: 'email', tab: 'templates', template: k }}>{k}</AdminLink></React.Fragment>)}
            </div>
          )}
          {t.ext?.note && <div className="text-xs text-grayscale-1100">{t.ext.note}</div>}
          {(t.description || t.notes) && <div className="text-xs text-grayscale-1100 whitespace-pre-wrap">{t.description || t.notes}</div>}
        </Section>}
        {!t.external && (t.notes || t.applies_to) && (
          <div className="text-xs text-grayscale-1100 whitespace-pre-wrap">{[t.applies_to, t.notes].filter(Boolean).join('\n')}</div>
        )}
      </Drawer>
    );
  }
  // Just created (the list is still reloading) or deleted elsewhere.
  if (!isNew && !t) {
    return (
      <Drawer title={id} onClose={onClose}>
        {note && <Msg kind={note.kind}>{note.text}</Msg>}
        <Loading label={`Loading ${id}… (if this stays, the trigger no longer exists)`} />
      </Drawer>
    );
  }

  const title = isNew ? 'New trigger' : `${t?.id} · ${t?.name}`;
  const subtitle = isNew ? 'Created Off and Manual — switching it on is a separate step with its own confirm'
    : `${categoryLabel(t?.category || '', ov?.categories || [])} · ${t?.builtin ? 'built-in' : 'custom'}`
      + `${t?.updated_at ? ` · last changed ${fmtIST(t.updated_at)}${t.updated_by ? ` by ${t.updated_by}` : ''}` : ''}`;
  const noContent = !t ? true : t.content_mode === 'template' ? !t.template_key || t.template_missing
    : !String(t.content?.subject || '').trim();
  const sendBlock = !t ? 'Save the trigger first'
    : changed ? 'Save or discard your edits first — the send uses the saved trigger'
      : noContent ? (t.template_missing ? `Template ${t.template_key} is not in the template store yet`
        : 'It has no e-mail yet — pick a template or write one, then save')
        : !t.can_send ? (t.on_hold ? `On hold — ${t.hold_reason || 'it cannot be sent'}` : 'This trigger cannot be sent') : '';

  return (
    <Drawer title={title} subtitle={subtitle} onClose={onClose}>
      {note && <Msg kind={note.kind}>{note.text}</Msg>}
      {richNote && <Msg kind={richNote.ok ? 'ok' : 'error'}>{richNote.node}</Msg>}

      {isNew && (
        <Banner tone="info">
          A new trigger is created <b>Off</b> and <b>Manual</b>. After it is created you can dry-run it, send a test to
          yourself, and switch it on — each switch-on asks first and says what will start happening.
        </Banner>
      )}
      {t && (
        <Section title="Switch" right={<StatePill t={t} gates={gatesClosed} />}>
          <TriggerSwitch t={t} ov={ov} labelled onChanged={() => onChanged(t.id)}
            blockOn={changed ? 'Save or discard your edits first' : ''}
            onError={e => setNote({ kind: 'error', text: e })} />
          <div className={`text-[11px] ${autoUnknown ? 'text-amber-800' : 'text-grayscale-1100'}`}>Right now: {rightNow()}</div>
          {t.enabled && t.enabled_at && (
            <div className="text-[11px] text-grayscale-1100">Switched on {fmtIST(t.enabled_at)}{t.enabled_by ? ` by ${t.enabled_by}` : ''}.</div>
          )}
          {t.manual_only && <div className="text-[11px] text-grayscale-1100">A broadcast: sent by hand only, it cannot be set to Auto.</div>}
        </Section>
      )}

      {t && sch && (
        <Section title="Next automatic send"
          right={changed ? <span className="text-[11px] text-amber-800">For the saved trigger — save to see your edits</span> : undefined}>
          <NextSendPanel s={sch.byId[t.id]} sch={sch} onElapsed={onElapsed} />
        </Section>
      )}

      {t && (
        <Section title="Check and send" right={<Btn small onClick={() => onOpenRuns(t.id)}>Runs & log</Btn>}>
          <div className="flex items-center gap-2 flex-wrap">
            <Btn onClick={runDry} disabled={busy !== ''}
              title={changed ? 'Runs the SAVED version — save first to check your edits' : 'Who would get it now, with reasons. Sends nothing.'}>
              {busy === 'dry' ? 'Checking…' : 'Dry run'}
            </Btn>
            {onPreviewCohorts && (
              <Btn onClick={() => onPreviewCohorts(t.id)} disabled={busy !== '' || noContent}
                title={changed ? 'Previews the SAVED trigger — save first to preview your edits'
                  : 'The mail each status × cohort segment would get now, for a member of it. Sends nothing, records nothing.'}>
                Preview by cohort
              </Btn>
            )}
            <Btn onClick={test} disabled={busy !== '' || noContent || !t.can_test}
              title="Sends this trigger's e-mail to your own address only — works while it is off">
              {busy === 'test' ? 'Sending…' : 'Send a test to me'}
            </Btn>
            <SendNow t={t} dry={dry} disabledReason={sendBlock || (busy ? 'Busy' : '')}
              onSent={(node, ok) => { setRichNote({ ok, node }); setDry(null); onChanged(t.id); }} />
            {t.enabled && t.mode === 'auto' && (
              <Btn kind="danger" onClick={() => { setRunErr(''); setRunAsk(true); }} disabled={busy !== '' || changed}
                title="Runs this automatic trigger once now">
                Run now…
              </Btn>
            )}
          </div>
          <div className="text-[11px] text-grayscale-1100">
            Dry run reads only (it may log a dry-run row). Last counted: {t.would_match === null ? 'never'
              : `${num(t.would_match)} would match${t.would_match_at ? ` (${fmtIST(t.would_match_at)})` : ''}`}.
            {t.sent_30d !== null && ` Sent in the last 30 days: ${num(t.sent_30d)}.`}
            {t.last_sent_at && ` Last sent ${fmtIST(t.last_sent_at)}.`}
          </div>
          {dry && <DryRunView r={dry} />}
        </Section>
      )}

      <Section title="When it fires">
        {!ov && <div className="text-xs text-amber-800">The signal list could not be read (MEMAC overview) — signal parameters cannot be edited.</div>}
        <div className="grid md:grid-cols-2 gap-3">
          <Labeled label="Name">
            <input value={draft.name} maxLength={120} onChange={e => edit({ name: e.target.value })} className={inputCls} />
          </Labeled>
          <Labeled label="Signal" hint="what makes someone due">
            <select value={draft.signal} onChange={e => setSignal(e.target.value)} className={inputCls}>
              <option value="">— pick a signal —</option>
              {signals.map(x => <option key={x.key} value={x.key}>{x.label}{x.manualOnly ? ' (manual only)' : ''}</option>)}
              {draft.signal && !sig && <option value={draft.signal}>{draft.signal}</option>}
            </select>
          </Labeled>
        </div>
        {sig?.help && <div className="text-[11px] text-grayscale-1100">{sig.help}</div>}
        {(sig?.params || []).length > 0 && (
          <div className="grid md:grid-cols-3 gap-3">
            {sig!.params.map(p => {
              const kind = kindOf(p, p.default);
              const v = draft.params[p.name] ?? '';
              const set = (x: string) => edit({ params: { ...draft.params, [p.name]: x } });
              return (
                <Labeled key={p.name} label={p.name.replace(/_/g, ' ')}
                  hint={kind === 'list' ? 'comma-separated' : p.default !== undefined && p.default !== null ? `default ${paramText(p.default, kind)}` : undefined}>
                  {kind === 'bool' ? (
                    <span className="h-9 flex items-center"><Check checked={v === 'true'} onChange={on => set(on ? 'true' : 'false')}>yes</Check></span>
                  ) : kind === 'select' && p.options?.length ? (
                    <select value={v} onChange={e => set(e.target.value)} className={inputCls}>
                      {p.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input value={v} onChange={e => set(e.target.value)} className={inputCls}
                      inputMode={kind === 'number' ? 'numeric' : undefined} spellCheck={false} />
                  )}
                  {p.help && <span className="text-[10px] text-grayscale-900">{p.help}</span>}
                </Labeled>
              );
            })}
          </div>
        )}
        <Labeled label="Stops once" hint="optional — someone who meets this is never sent it again">
          {stopOpts.length ? (
            <select value={draft.stop_signal} className={inputCls}
              onChange={e => edit({ stop_signal: e.target.value, stop_params: stopParamsFrom(e.target.value, t?.raw?.stop_signal, ov) })}>
              <option value="">— no stop condition —</option>
              {stopOpts.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              {draft.stop_signal && !stopOpts.some(o => o.key === draft.stop_signal) && (
                <option value={draft.stop_signal}>{draft.stop_signal}</option>
              )}
            </select>
          ) : (
            <input value={draft.stop_signal} onChange={e => edit({ stop_signal: e.target.value.trim() })}
              placeholder="e.g. has_interests (leave empty for none)" spellCheck={false} className={inputCls} />
          )}
        </Labeled>
        {draft.stop_signal && stopSchema(draft.stop_signal, ov).map(p => (
          <Labeled key={p.name} label={`Stop: ${p.name.replace(/_/g, ' ')}`} hint={p.help || undefined}>
            <input value={draft.stop_params[p.name] ?? ''} inputMode="numeric" spellCheck={false}
              onChange={e => edit({ stop_params: { ...draft.stop_params, [p.name]: e.target.value } })}
              className={`${inputCls} !w-40`} />
          </Labeled>
        ))}
      </Section>

      <Section title="Who it is for">
        <p className="text-xs text-grayscale-1100 -mt-1">
          On top of the signal. Our team, bounced and inactive accounts are always left out. Cohorts use the raw
          profile score and the searches count, not the floored profile % the tags use.
        </p>
        <CheckList label="Cohort" options={(ov?.cohorts || []).map(c => c.key)}
          labels={Object.fromEntries((ov?.cohorts || []).map(c => [c.key, c.label]))}
          values={draft.cohort} onChange={v => edit({ cohort: v })} />
        <TagFilter value={draft.tf} counts={false} hide={['profile']}
          onChange={v => edit({ tf: v })} />
        <div className="text-[11px] text-grayscale-1100">
          {[...describeTagFilter(draft.tf), ...(draft.cohort.length ? [`Cohort ${draft.cohort.join(', ')}`] : [])].join(' · ')
            || 'Everyone the signal finds.'}
          {tagFilterActive(draft.tf) && (
            <button type="button" className="ml-2 underline underline-offset-2" onClick={() => edit({ tf: EMPTY_TAG_FILTER })}>clear</button>
          )}
        </div>
      </Section>

      <Section title="What it sends" right={
        <span className="flex items-center gap-2">
          <Btn small onClick={() => setPicker(true)} disabled={busy !== ''}
            title="A saved template, a built-in e-mail, a past campaign or a design — sent as it is, or copied here to change">
            Start from…
          </Btn>
          <Btn small onClick={preview} disabled={busy !== ''}
            title="Shows the e-mail as this trigger would send it, your unsaved edits included. Sends nothing.">
            {busy === 'preview' ? 'Rendering…' : 'Preview'}
          </Btn>
        </span>
      }>
        <div className="flex gap-4 flex-wrap text-sm">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="radio" name="memac-content" checked={!ownCopy}
              onChange={() => edit({ content_mode: 'template' })} className="accent-primary-500" />
            A saved template
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="radio" name="memac-content" checked={ownCopy} onChange={writeOwnCopy} className="accent-primary-500" />
            Written for this trigger
          </label>
        </div>
        {!ownCopy && (
          <>
            <Labeled label="Template" hint="MEMAC's own first, then Saved templates (shared with Campaigns), then the other e-mails">
              <select value={draft.template_key} onChange={e => edit({ template_key: e.target.value })} className={inputCls}>
                <option value="">— pick a template —</option>
                {tplGroups.map(g => (
                  <optgroup key={g.group} label={g.group}>
                    {g.items.map(x => (
                      <option key={x.key} value={x.key}>
                        {x.name}{x.library ? (x.version ? ` · v${x.version}` : '') : ` (${x.key})`}
                      </option>
                    ))}
                  </optgroup>
                ))}
                {draft.template_key && !tpl && (
                  <option value={draft.template_key}>
                    {(t?.template_key === draft.template_key && t.template_name) || draft.template_key}
                    {t?.template_key === draft.template_key && t.template_archived ? ' (archived)' : ''}
                  </option>
                )}
              </select>
            </Labeled>
            {templatesError && <div className="text-[11px] text-red-700">Templates: {templatesError}</div>}
            {t?.template_missing && draft.template_key === t.template_key && (
              <div className="text-[11px] text-amber-800">
                “{t.template_key}” is not in the template store on this server yet, so this trigger cannot be sent or tested.
              </div>
            )}
            {t?.template_archived && draft.template_key === t.template_key && (
              <div className="text-[11px] text-amber-800">
                “{t.template_name || t.template_key}” is archived in Saved templates. This trigger still sends it, but once you
                pick another it cannot be picked again until it is unarchived.
              </div>
            )}
            {tpl && (
              <div className="text-[11px] text-grayscale-1100 flex flex-wrap gap-x-3 gap-y-1 items-center">
                {tpl.library && (
                  <Pill tone="blue" title="A Saved template, shared with Campaigns">
                    Saved template · {FORMAT_LABEL[tpl.format]}{tpl.version ? ` · v${tpl.version}` : ''}
                  </Pill>
                )}
                <span>Primary button: {tpl.primary_cta ? <b>{tpl.primary_cta}</b> : 'none'}{tpl.primary_cta_url ? ` → ${tpl.primary_cta_url}` : ''}</span>
                {!tpl.send_html && <Pill tone="warn">plain text — opens cannot be counted</Pill>}
                {(tpl.primary_cta_url || '').startsWith('mailto:') && <Pill tone="warn">mailto button — clicks cannot be counted</Pill>}
                {tpl.category && tpl.category !== draft.category && (
                  <Pill tone="warn" title="The trigger's category still decides who can unsubscribe and whether the status cap applies">
                    written as {categoryLabel(tpl.category, ov?.categories || []).toLowerCase()} mail
                  </Pill>
                )}
                {alsoUsing.length > 0 && (
                  <Pill tone="mute" title={alsoUsing.map(x => `${x.id} · ${x.name}${x.enabled ? ' (on)' : ''}`).join('\n')}>
                    also sent by {alsoUsing.length} other trigger{alsoUsing.length === 1 ? '' : 's'}
                  </Pill>
                )}
              </div>
            )}
            {draft.template_key && (
              <div className="flex items-center gap-2 flex-wrap">
                {draft.template_key.startsWith(MEMAC_KEY_PREFIX) || draft.template_key.startsWith(LIBRARY_KEY_PREFIX) || tpl?.library
                  ? <Btn small onClick={() => leaveFor(() => onOpenTemplates(draft.template_key))}
                    title={draft.template_key.startsWith(MEMAC_KEY_PREFIX) ? 'MEMAC → Templates' : 'MEMAC → Templates → Saved templates'}>
                    Edit template
                  </Btn>
                  : <AdminLink to={{ group: 'email', tab: 'templates', template: draft.template_key }}>Edit it in Templates (editable)</AdminLink>}
                <Btn small onClick={copyTemplateIn} disabled={busy !== ''}
                  title="Its words become this trigger's own copy, to change here. The template stays as it is.">
                  {busy === 'source' ? 'Copying…' : 'Copy into this trigger'}
                </Btn>
                <span className="text-[11px] text-grayscale-1100">
                  An edit to the template reaches this trigger on its next send
                  {alsoUsing.some(x => x.enabled) ? ' (and the other switched-on triggers that send it)' : ''}.
                </span>
              </div>
            )}
            {tplPh && tplPh.key === phKey && (
              <FallbacksPanel names={tplPh.names} value={draft.fallbacks} inherited={tplPh.inherited}
                onChange={fb => edit({ fallbacks: fb })} unsetLabel="the template's"
                title="Fallbacks for this trigger (on top of the template's)" />
            )}
            {tplPh?.error && tplPh.key === phKey && (
              <div className="text-[11px] text-red-700">Its placeholders could not be read: {tplPh.error}</div>
            )}
            {!copyEmpty(draft.copy) && (
              <div className="text-[11px] text-grayscale-1100">
                Its own copy (“{draft.copy.subject || 'no subject'}”) stays stored, unused, and comes back with Written for this trigger.
              </div>
            )}
          </>
        )}
        {ownCopy && (
          <>
            {draft.source && (
              <div className="text-[11px] text-grayscale-1100 flex flex-wrap gap-x-2 gap-y-1 items-center">
                <span>
                  Started from {SOURCE_KIND_LABEL[draft.source.kind] || draft.source.kind} “{draft.source.name || draft.source.ref}”
                  {draft.source.version ? ` v${draft.source.version}` : ''} — a copy: later edits there do not change it.
                </span>
                {copyStale && (
                  <>
                    <Pill tone="warn">the template is now v{srcNow?.version}</Pill>
                    <button type="button" className="underline underline-offset-2 text-primary-700" disabled={busy !== ''}
                      onClick={() => { void takeNewVersion(); }}>Take the new version</button>
                  </>
                )}
              </div>
            )}
            {designs.error && <div className="text-[11px] text-red-700">Email designs: {designs.error}</div>}
            {!designs.data && !designs.error
              ? <Loading label="Loading the editor…" />
              : (
                <ContentEditor value={draft.copy} onChange={editCopy} formats={['html', 'design']}
                  placeholders={placeholders} designs={designs.data || undefined} allowSaveAsDesign
                  saveAsDesignNote={`From the MEMAC trigger “${draft.name || t?.id || 'new'}”`} />
              )}
            <EnvelopeFields value={draft.env} onChange={editEnv} senders={senders} sendersError={sendersRes.error} />
            {!draft.env.from_email && senders.length > 0 && (
              <div className="text-[11px] text-amber-800">
                No From picked: it goes from {senders[0].email}, the first approved sender. Pick one above to be sure.
              </div>
            )}
            <FallbacksPanel names={fallbackable(draft.copy)} value={draft.fallbacks} onChange={fb => edit({ fallbacks: fb })} />
            <div className="flex items-center gap-2 flex-wrap">
              <Btn small onClick={() => {
                const problem = contentProblem(draft.copy);
                if (problem) { setNote({ kind: 'error', text: `Not saved as a template: ${problem}` }); return; }
                setSaveAs(true);
              }} disabled={busy !== ''}>Save as a new template…</Btn>
              <span className="text-[11px] text-grayscale-1100">
                Adds it to Saved templates, shared with Campaigns, so other triggers and campaigns can send it.
              </span>
            </div>
          </>
        )}
        {pvOpen && (
          <PreviewPanel r={pv?.r || null} error={pvErr} busy={busy === 'preview'} stale={!!pv && pv.sig !== pvSig}
            as={pvAs} onAs={setPvAs} onRefresh={() => { void preview(); }}
            onClose={() => { setPvOpen(false); setPv(null); setPvErr(''); }} />
        )}
        <Labeled label="Applies to (as listed)" hint="shown in the table; who gets it is the audience above">
          <input value={draft.applies_to} maxLength={300} onChange={e => edit({ applies_to: e.target.value })}
            placeholder="e.g. Explorer · Cohort C, D" className={inputCls} />
        </Labeled>
        <Labeled label="CTA (table wording)" hint="for the table; the e-mail's buttons are its own">
          <input value={draft.cta_label} maxLength={120} onChange={e => edit({ cta_label: e.target.value })} className={inputCls} />
        </Labeled>
      </Section>

      <Section title="How often">
        <div className="grid md:grid-cols-4 gap-3">
          <Labeled label="Max per 30 days" hint="per person">
            <input value={draft.max_per_30d} inputMode="numeric" onChange={e => edit({ max_per_30d: e.target.value })} className={inputCls} />
          </Labeled>
          <Labeled label="Max in a lifetime" hint="1 = once">
            <input value={draft.max_lifetime} inputMode="numeric" onChange={e => edit({ max_lifetime: e.target.value })} className={inputCls} />
          </Labeled>
          <Labeled label="Cooldown (days)" hint="between two">
            <input value={draft.cooldown_days} inputMode="numeric" onChange={e => edit({ cooldown_days: e.target.value })} className={inputCls} />
          </Labeled>
          <Labeled label="Per occurrence" hint="e.g. per renewal">
            <input value={draft.per_occurrence} inputMode="numeric" onChange={e => edit({ per_occurrence: e.target.value })} className={inputCls} />
          </Labeled>
        </div>
        <div className="text-[11px] text-grayscale-1100">
          Reads as: {(() => {
            try {
              return capText({
                max_per_30d: capInt('', draft.max_per_30d), max_lifetime: capInt('', draft.max_lifetime),
                cooldown_days: capInt('', draft.cooldown_days), per_occurrence: capInt('', draft.per_occurrence),
              });
            } catch { return 'a cap is not a whole number'; }
          })()}. Blank = no such cap.
        </div>
        <div className="grid md:grid-cols-2 gap-3 items-end">
          <Labeled label="Category">
            <select value={draft.category} onChange={e => edit({ category: e.target.value })} className={inputCls}>
              {(ov?.categories || []).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              {!(ov?.categories || []).some(c => c.key === draft.category) && <option value={draft.category}>{draft.category}</option>}
            </select>
          </Labeled>
          <div className="text-xs text-grayscale-1100 pb-2">
            {SERVICE_CATS.includes(draft.category)
              ? <>A <b>service</b> mail: exempt from the status cap and from category unsubscribes.</>
              : <>Counts toward the status cap ({s?.status_cap_30d ?? 6} non-service mails per member per 30 days); members can unsubscribe from this category.</>}
          </div>
        </div>
      </Section>

      <Section title="Notes">
        <textarea value={draft.notes} rows={3} onChange={e => edit({ notes: e.target.value })}
          placeholder="Why it exists, what to check before switching it on…"
          className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 w-full" />
      </Section>

      <div className="sticky -bottom-6 -mx-6 -mb-6 px-6 py-3 bg-grayscale-100 border-t border-grayscale-300 flex items-center gap-2 flex-wrap">
        <Btn kind="primary" onClick={save} disabled={busy !== '' || (!isNew && !changed)}>
          {busy === 'save' ? 'Saving…' : isNew ? 'Create (Off, Manual)' : 'Save'}
        </Btn>
        {changed && !isNew && <Btn onClick={discard} disabled={busy !== ''}>Discard changes</Btn>}
        {changed && <span className="text-[11px] text-amber-800">Unsaved changes.</span>}
        <span className="flex-1" />
        {t?.can_reset && <DangerBtn small onConfirm={reset}>{busy === 'reset' ? 'Resetting…' : 'Reset to built-in'}</DangerBtn>}
        {t?.can_delete && <DangerBtn small onConfirm={del}>{busy === 'delete' ? 'Deleting…' : 'Delete trigger'}</DangerBtn>}
      </div>

      {runAsk && t && (
        <ConfirmDialog title={`Run ${t.id} now?`} danger busy={busy === 'run'} error={runErr}
          confirmLabel="Run now — send real e-mails"
          ack="I understand this e-mails every eligible member now."
          onClose={() => setRunAsk(false)} onConfirm={runNow}
          lines={[
            <><b>{t.id} · {t.name}</b> runs once now and e-mails every member it finds eligible: {contentText(t)}.</>,
            t.would_match === null ? <>Who that is has not been counted — Cancel and run a Dry run to see.</>
              : <>The last dry run counted {num(t.would_match)}.</>,
            <>Every cap, exclusion and the server gate still apply{s?.max_per_run ? `, at most ${num(s.max_per_run)} e-mails` : ''};
              only the global switch is not waited for.</>,
            ov && !ov.envAuto ? <>The server setting MEMAC_AUTO=1 is missing, so the server will refuse and nothing is sent.</> : null,
          ]} />
      )}

      {picker && inBody(
        <TemplatePicker allowReference formats={['html', 'design']} memacCategory={draft.category}
          title="Start this trigger's e-mail from…" onClose={() => setPicker(false)} onPick={onPicked} />,
      )}
      {askFormat && inBody(
        <Modal title={`Copy “${askFormat.name}” as…`} onClose={() => setAskFormat(null)}
          footer={<>
            <Btn onClick={() => setAskFormat(null)}>Cancel</Btn>
            <Btn onClick={() => { void takeAs(askFormat, 'html'); }}>Rich text</Btn>
            <Btn kind="primary" onClick={() => { void takeAs(askFormat, 'design'); }}>Design</Btn>
          </>}>
          <p>
            It is a plain-text e-mail with {askFormat.ctas.length === 1 ? 'a button' : `${askFormat.ctas.length} buttons`}.
            {' '}<b>Design</b> keeps the buttons and opens it in the block builder. <b>Rich text</b> turns them into links.
          </p>
          <p className="text-xs text-grayscale-1100">Either way it becomes this trigger's own copy; the original stays as it is.</p>
        </Modal>,
      )}
      {saveAs && inBody(
        <SaveAsTemplate content={draft.copy} envelope={draft.env} fallbacks={draft.fallbacks} origin="memac"
          source={t ? { kind: 'trigger', ref: t.id, name: t.name, at: new Date().toISOString() } : null}
          defaultName={draft.name.trim() || draft.copy.subject.trim()} defaultCategory={draft.category}
          note="The e-mail as it is here, unsaved edits included, with its From and Reply-To. It joins Saved templates, shared with Campaigns."
          onClose={() => setSaveAs(false)}
          onSaved={(row, warnings) => {
            setSaveAs(false);
            setSavedTpl({ key: String(row?.key || ''), name: String(row?.name || ''), warnings: (warnings || []).map(String) });
            onChanged();
          }} />,
      )}
      {savedTpl && inBody(
        <Modal title="Saved as a template" onClose={() => setSavedTpl(null)}
          footer={<>
            <Btn onClick={() => setSavedTpl(null)}>Done</Btn>
            <Btn kind="primary" disabled={!savedTpl.key} onClick={() => {
              edit({ content_mode: 'template', template_key: savedTpl.key });
              setNote({ kind: 'ok', text: `Now sends the saved template “${savedTpl.name}” — save the trigger to keep it. `
                + 'Its own copy stays stored, unused.' });
              setSavedTpl(null);
            }}>Use it for this trigger</Btn>
          </>}>
          <p>
            “{savedTpl.name}” is in Saved templates — MEMAC → Templates and Email Center → Saved templates — and Campaigns
            can send it too.
          </p>
          <p className="text-xs text-grayscale-1100">
            This trigger still sends its own copy. <b>Use it for this trigger</b> sends the saved template instead, so a
            later edit to the template reaches it; then save the trigger.
          </p>
          {savedTpl.warnings.map((w, i) => <div key={i} className="text-xs text-amber-800">{w}</div>)}
        </Modal>,
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// Pieces of "What it sends"
// ---------------------------------------------------------------------------

/** The trigger's mail as memac_preview rendered it: read-only, sample links. */
const PreviewPanel: React.FC<{
  r: Rendered | null; error: string; busy: boolean;
  /** The draft (or "Preview as") changed since this render. */
  stale: boolean;
  as: string; onAs: (v: string) => void; onRefresh: () => void; onClose: () => void;
}> = ({ r, error, busy, stale, as, onAs, onRefresh, onClose }) => {
  const who = r ? renderedFor(r) : '';
  const member = !!who && who !== 'sample values';
  return (
    <div className="flex flex-col gap-2 border-t border-grayscale-200 pt-3">
      <div className="flex items-end gap-2 flex-wrap">
        <Labeled label="Preview as" hint="a member's e-mail; blank = sample values">
          <input value={as} onChange={e => onAs(e.target.value)} placeholder="name@example.com" spellCheck={false}
            onKeyDown={e => { if (e.key === 'Enter') onRefresh(); }} className={`${inputCls} !w-64`} />
        </Labeled>
        <Btn small onClick={onRefresh} disabled={busy}>{busy ? 'Rendering…' : r ? 'Refresh' : 'Preview'}</Btn>
        <Btn small onClick={onClose}>Close preview</Btn>
      </div>
      {error && <Msg kind="error">Preview failed: {error}</Msg>}
      {r && stale && !busy && <div className="text-[11px] text-amber-800">Changed since this preview — Refresh to see it.</div>}
      {r && (r.warnings || []).map((w, i) => <div key={i} className="text-xs text-amber-800">{w}</div>)}
      {r && <VaryingNotices varying={r.varying} />}
      {r && (r.blank || []).length > 0 && (
        <div className="text-[11px] text-grayscale-1100">
          Blank for {member ? who : 'the sample'}: {(r.blank || []).map(x => `{${x}}`).join(', ')}.
        </div>
      )}
      {r && (
        <EmailPreview subject={r.subject} html={r.html} text={r.text} from={fmtFrom(r.from_name, r.from_email)}
          replyTo={r.reply_to} to={member ? who : undefined} plainOnly={!r.send_html} height={520}
          banner={(
            <div className="text-[11px] text-grayscale-1100">
              Filled in from {member ? `${who}'s account` : 'sample values'}.
              {r.footer_added ? ' MEMAC adds the unsubscribe line at the bottom.' : ''}
              {' '}The links here are samples: a click unsubscribes nobody and logs nothing. Nothing was sent.
            </div>
          )} />
      )}
    </div>
  );
};

export default TriggerEditor;
